import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { authApi } from '../services/api.service';
import { tokenManager } from '../lib/tokenManager';
import { toast } from '../components/ui/toast';

// User type matching backend response
export interface AuthUser {
  user_id: string;
  email: string;
  name: string;
  role: string;
  allowedCompanyIds: string[];
}

// Auth context type
interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth Provider Component
 * Manages authentication state globally with secure token handling
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is authenticated
  const isAuthenticated = !!user;

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Refresh user from server
  const refreshUser = useCallback(async () => {
    try {
      // Check if we have a session (refresh token in cookie)
      const userData = await authApi.checkSession();
      if (userData) {
        setUser({
          user_id: userData.user_id,
          email: userData.email,
          name: userData.name,
          role: userData.role,
          allowedCompanyIds: userData.allowedCompanyIds || [],
        });
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Login function
  const login = useCallback(async (email: string, password: string, remember = false) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authApi.login({ email, password, remember });
      
      if (response?.user) {
        setUser({
          user_id: response.user.user_id,
          email: response.user.email,
          name: response.user.name,
          role: response.user.role,
          allowedCompanyIds: response.user.allowedCompanyIds || [],
        });
        toast.success(`Selamat datang, ${response.user.name}!`, 'Login Berhasil');
      }
    } catch (err: unknown) {
      // Don't expose raw server errors
      const message = 'Email atau kata sandi tidak valid.';
      setError(message);
      toast.error(message, 'Login Gagal');
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      await authApi.logout();
      toast.info('Anda telah keluar dari sistem.', 'Logout');
    } finally {
      setUser(null);
      setError(null);
    }
  }, []);

  // Listen for auth:logout events (from API interceptor)
  useEffect(() => {
    const handleLogout = () => {
      setUser(null);
      setError(null);
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, []);

  // Check authentication on mount
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    refreshUser,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

/**
 * HOC for protected routes
 */
export const withAuth = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  requiredRoles?: string[]
): React.FC<P> => {
  return function WithAuthComponent(props: P) {
    const { isAuthenticated, isLoading, user } = useAuth();

    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
            <p className="text-gray-500 mt-2">Please log in to continue.</p>
          </div>
        </div>
      );
    }

    if (requiredRoles && user && !requiredRoles.includes(user.role)) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900">Unauthorized</h2>
            <p className="text-gray-500 mt-2">You don't have permission to access this page.</p>
          </div>
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
};

export default AuthContext;
